// Tier 2 draftRosters migration. Live replacement for the draftRosters portion of App.jsx's former
// custom watcher (onSnapshot(doc(db,"warriors","draftRosters"), ...), which read a single
// warriors/draftRosters document's `draftRosters` map field keyed by "teamId::tournamentId" — see
// draftKey() in App.jsx). This module subscribes to the top-level `draftRosters` collection (one
// document per entry, doc ID = the same compound key — see scripts/migrate-draftrosters.cjs) and
// reconstructs the exact same in-memory shape: {[key]: entry}. Every existing read site
// (`draftRosters[key]`, `Object.entries`, `.players`, `.lineups`, etc.) keeps working unchanged,
// since entries are still plain objects, not wrapped in any extra envelope.
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../firebase';

// Converts one draftRosters/{key} document into the plain entry object the app's in-memory
// draftRosters[key] value has always been. `teamId`/`tournamentId` (added by Phase A) stay dropped
// — the key itself already encodes team/tournament, and nothing client-side reads them as entry
// fields, so re-deriving them from the key rather than carrying duplicate copies is still correct.
// `nodeId` USED to be dropped for the same reason ("nothing client-side reads it yet") — fixed
// proactively alongside players/teams/coaches/practiceGroups/practiceCancellations, same reasoning
// (see practiceGroupsCollection.js). Checked every write site that touches a draftRosters entry
// (setRoster, saveDraftRosterLineups, Finalize, gear assignment, etc., src/App.jsx) — all of them
// build targeted field-specific payloads (players/lineups/isFinalized/gearAssignments/etc.) rather
// than spreading a whole entry object, so carrying nodeId through here doesn't leak it into any
// write that shouldn't have it.
export function draftRostersDocToRecord(data) {
  const { teamId, tournamentId, ...rest } = data;
  return rest;
}

// Live-subscription replacement for the draftRosters portion of the custom watcher in App.jsx —
// same ergonomics (takes the setter, returns an unsubscribe function). Kept as the unscoped variant
// for coach/parent (neither has a node concept), matching draftRosters' read rule staying
// (isAdmin()&&nodeIsDescendantOrSelf(...))||isCoach()||isParent() — admin now uses
// watchDraftRostersCollectionForNodes below.
export function watchDraftRostersCollection(setter) {
  return onSnapshot(collection(db, 'draftRosters'), (snap) => {
    const map = {};
    snap.docs.forEach((d) => { map[d.id] = draftRostersDocToRecord(d.data()); });
    setter(map);
  });
}

// Node-scoped variant (mirroring watchTeamsCollectionForNodes/watchBaseRostersCollectionForNodes
// exactly) — constrained via where('nodeId','in',nodeIds) to a caller-supplied permitted-node list.
// Used for the admin role only. Same 30-value 'in' operator ceiling as the other ForNodes variants.
export function watchDraftRostersCollectionForNodes(nodeIds, setter) {
  return onSnapshot(query(collection(db, 'draftRosters'), where('nodeId', 'in', nodeIds)), (snap) => {
    const map = {};
    snap.docs.forEach((d) => { map[d.id] = draftRostersDocToRecord(d.data()); });
    setter(map);
  });
}
